import React from "react";
import { useSearchParams } from "react-router-dom";
import { FaCalendar, FaClipboardList, FaDollarSign, FaListAlt, FaTable } from "react-icons/fa";
import SEOHead from "../../components/SEO/SEOHead";
import TreeBoard from "../../components/Brackets/TreeBoard";
import TeamScoreCard from "../../components/ScoringHub/Views/TeamScoreCard";
import PayNowPanel from "../../components/Signup/PayNowPanel";
import DesktopTable from "../../components/BowlerAveragesTable/DesktopTable";
import PayoutTableWorkspace from "../../components/organizations/PayoutTables/PayoutTableWorkspace";
import { usePayoutWorkspace } from "../../components/organizations/PayoutTables/usePayoutWorkspace";
import { Card } from "../../components/UI/DesignStandards";
import {
  DashboardHeader,
  DashboardTabs,
  SignupCard,
} from "../UserSignupDashboard/components";
import {
  SignupFilters,
  SignupList,
} from "../../components/Admin/SignupDashboard/components";
import {
  DEMO_TOURNAMENT_NAME,
  demoAverageBowlers,
  demoAverageHandlers,
  demoBowlers,
  demoCalculationResult,
  demoCartLine,
  demoDirectorFilters,
  demoDirectorOnSquad,
  demoDirectorSignups,
  demoResolvedTree,
  demoScoringTeam,
  demoSignup,
  demoTickets,
} from "./productShotFixtures";

const tabClass = (active) =>
  `flex items-center gap-2 py-3 px-3 sm:px-4 border-b-2 font-display font-bold text-sm tracking-wide whitespace-nowrap ${
    active
      ? "border-brand-charcoal text-brand-charcoal"
      : "border-transparent text-brand-muted"
  }`;

const Shot = ({ id, children }) => (
  <div
    id={`shot-${id}`}
    data-product-shot={id}
    className="bg-brand-cream p-6 max-w-5xl mx-auto"
  >
    {children}
  </div>
);

const ProductShots = () => {
  const [searchParams] = useSearchParams();
  const only = searchParams.get("shot");
  const workspace = usePayoutWorkspace({ canEdit: false });
  const {
    showMore,
    setShowMore,
    advanced,
    planning,
    payoutData,
    isEditing,
    isRecommended,
    optionsLocked,
    calculateColumnTotal,
    isColumnValid,
    handlePercentageChange,
    handlePlanningField,
    handleAdvancedField,
    handleResetRecommended,
    handleAddColumn,
    handleDeleteColumn,
    handleStartEditCells,
    handleFinishEditing,
    handleCancelEditing,
  } = workspace;

  const show = (id) => !only || only === id;

  return (
    <>
      <SEOHead
        title="Product shots"
        description="Demo screenshots with fake names."
        canonicalUrl="/internal/product-shots"
        noIndex
        noFollow
      />
      <div className="min-h-screen bg-brand-cream py-8 space-y-16">
        {show("signups") ? (
        <Shot id="signups">
          <div className="space-y-6">
            <DashboardHeader>
              <DashboardTabs
                activeTab="due"
                onTabChange={() => {}}
                counts={{ all: 1, due: 1, upcoming: 1, past: 0, cancelled: 0 }}
                showDue
              />
            </DashboardHeader>
            <div className="lg:grid lg:grid-cols-3 lg:gap-8 lg:items-start">
              <aside className="order-last lg:order-none lg:col-start-3 lg:row-start-1 space-y-6">
                <Card className="overflow-hidden" padding="">
                  <div className="bg-brand-charcoal text-brand-cream px-6 py-4">
                    <h2 className="font-display font-bold text-lg text-brand-cream tracking-tight">
                      Pay now
                    </h2>
                    <p className="text-sm text-brand-mist mt-1">
                      {DEMO_TOURNAMENT_NAME}
                    </p>
                    <p className="text-sm text-brand-mist">1 due</p>
                  </div>
                  <div className="p-6">
                    <PayNowPanel
                      tournamentId="demo-tournament"
                      lines={[demoCartLine]}
                      payNowEnabled
                      statusKnown
                      hideTitle
                      showLegalNote={false}
                    />
                  </div>
                </Card>
              </aside>
              <div className="lg:col-span-2">
                <div className="card-board overflow-hidden">
                  <ul className="divide-y divide-brand-charcoal/10">
                    <SignupCard
                      signup={demoSignup}
                      onViewSignup={() => {}}
                      onCancelSignup={() => {}}
                    />
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </Shot>
        ) : null}

        {show("payments") ? (
        <Shot id="payments">
          <div className="space-y-6">
            <div className="card-board overflow-hidden">
              <div className="bg-brand-charcoal text-brand-cream px-6 py-4">
                <h1 className="font-display font-bold text-xl text-brand-cream tracking-tight">
                  Signup Dashboard
                </h1>
                <p className="text-sm text-brand-mist mt-1">
                  Squads, waitlist, and who still owes.
                </p>
              </div>
              <div className="p-4 sm:p-6">
                <SignupFilters
                  showFilters={false}
                  setShowFilters={() => {}}
                  filters={demoDirectorFilters}
                  searchTerm=""
                  setSearchTerm={() => {}}
                  signups={demoDirectorSignups}
                  clearFilters={() => {}}
                  setQuickFilter={() => {}}
                  removeFilter={() => {}}
                  handleFilterChange={() => {}}
                />
              </div>
            </div>
            <SignupList
              signups={demoDirectorOnSquad}
              allSignups={demoDirectorSignups}
              actionId={null}
              onViewSignup={() => {}}
              onMarkPaid={() => {}}
              onMarkUnpaid={() => {}}
            />
          </div>
        </Shot>
        ) : null}

        {show("scoring") ? (
        <Shot id="scoring">
          <div className="space-y-6">
            <div className="card-board overflow-hidden">
              <div className="bg-brand-charcoal text-brand-cream px-6 py-4">
                <h1 className="font-display font-bold text-xl text-brand-cream tracking-tight">
                  Scoring
                </h1>
                <p className="text-sm text-brand-mist mt-1">
                  Enter scores and assign lanes.
                </p>
              </div>
              <nav className="flex overflow-x-auto px-2 sm:px-4" aria-label="Scoring tabs">
                <span className={tabClass(true)}>
                  <FaClipboardList className="w-4 h-4 text-brand-muted" />
                  <span>Scores</span>
                </span>
                <span className={tabClass(false)}>
                  <span>Lanes</span>
                </span>
              </nav>
            </div>
            <TeamScoreCard
              team={demoScoringTeam}
              onScoreUpdate={() => {}}
              onTeamDelete={() => {}}
              onVerificationChange={() => {}}
              calculatingTotals={new Set()}
              getCalculationResult={demoCalculationResult}
              canManage={false}
              canEnterScores
              tournamentId="demo-tournament"
              isScoreSaving={() => false}
            />
          </div>
        </Shot>
        ) : null}

        {show("brackets") ? (
        <Shot id="brackets">
          <TreeBoard
            tickets={demoTickets}
            bowlers={demoBowlers}
            resolvedTrees={[demoResolvedTree]}
          />
        </Shot>
        ) : null}

        {show("payouts") ? (
        <Shot id="payouts">
          <div className="space-y-6">
            <div className="card-board overflow-hidden">
              <div className="bg-brand-charcoal text-brand-cream px-6 py-4">
                <h1 className="font-display font-bold text-xl text-brand-cream tracking-tight">
                  Finance
                </h1>
                <p className="text-sm text-brand-mist mt-1">
                  Payouts, added money, and payout table. Pays 1 in 6 bowlers.
                </p>
              </div>
              <nav className="flex overflow-x-auto px-2 sm:px-4" aria-label="Finance tabs">
                <span className={tabClass(false)}>
                  <FaListAlt className="w-4 h-4 text-brand-muted" />
                  <span>Payouts</span>
                </span>
                <span className={tabClass(false)}>
                  <FaDollarSign className="w-4 h-4 text-brand-muted" />
                  <span>Added money</span>
                </span>
                <span className={tabClass(true)}>
                  <FaTable className="w-4 h-4 text-brand-muted" />
                  <span>Payout table</span>
                </span>
              </nav>
            </div>
            <PayoutTableWorkspace
              canEdit={false}
              showMore={showMore}
              onToggleMore={() => setShowMore((open) => !open)}
              places={advanced.places}
              shape={advanced.shape}
              ratioNumerator={advanced.ratioNumerator}
              ratioDenominator={advanced.ratioDenominator}
              payoutData={payoutData}
              planning={planning}
              onPlanningChange={handlePlanningField}
              isEditing={isEditing}
              onChangeField={handleAdvancedField}
              isRecommended={isRecommended}
              onResetRecommended={handleResetRecommended}
              optionsLocked={optionsLocked}
              onEditCells={handleStartEditCells}
              onSave={handleFinishEditing}
              onCancel={handleCancelEditing}
              canSave={false}
              allowPersist={false}
              idPrefix="demo-payout"
              calculateColumnTotal={calculateColumnTotal}
              isColumnValid={isColumnValid}
              onPercentageChange={handlePercentageChange}
              onAddColumn={handleAddColumn}
              onDeleteColumn={handleDeleteColumn}
            />
          </div>
        </Shot>
        ) : null}

        {show("overview") ? (
        <Shot id="overview">
          <div className="space-y-6">
            <div className="card-board overflow-hidden">
              <div className="bg-brand-charcoal text-brand-cream px-6 py-6 md:px-8">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                  <div>
                    <h1 className="text-4xl font-display font-bold text-brand-cream tracking-tight">
                      Saturday Singles
                    </h1>
                    <div className="flex items-center mt-3 text-brand-mist">
                      <FaCalendar className="mr-2 text-brand-mist" />
                      <span className="font-medium">
                        Sunday, November 8, 2026
                      </span>
                    </div>
                  </div>
                  <div className="mt-4 md:mt-0 md:text-right">
                    <div className="inline-flex items-center px-3 py-1.5 text-sm font-display font-bold tracking-wide rounded-lg border border-brand-gold text-brand-gold">
                      <FaCalendar className="mr-1.5 text-xs" />
                      Registration Open
                    </div>
                    <p className="text-xs text-brand-mist mt-1 md:text-right max-w-xs">
                      Online signup is open.
                    </p>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4">
                <div className="text-center px-4 py-5 border-b border-r border-brand-charcoal/10 lg:border-b-0">
                  <div className="font-score text-3xl text-brand-charcoal mb-1">
                    1
                  </div>
                  <div className="text-xs font-display tracking-wider uppercase text-brand-muted">
                    Squads
                  </div>
                </div>
                <div className="text-center px-4 py-5 border-b border-brand-charcoal/10 lg:border-b-0 lg:border-r">
                  <div className="font-score text-3xl text-brand-charcoal mb-1">
                    8
                  </div>
                  <div className="text-xs font-display tracking-wider uppercase text-brand-muted">
                    Teams
                  </div>
                </div>
                <div className="text-center px-4 py-5 border-r border-brand-charcoal/10">
                  <div className="font-score text-3xl text-brand-charcoal mb-1">
                    8
                  </div>
                  <div className="text-xs font-display tracking-wider uppercase text-brand-muted">
                    Bowlers
                  </div>
                </div>
                <div className="text-center px-4 py-5">
                  <div className="font-score text-3xl text-brand-charcoal mb-1">
                    $360
                  </div>
                  <div className="text-xs font-display tracking-wider uppercase text-brand-muted">
                    Prize Fund
                  </div>
                </div>
              </div>
            </div>
            <div className="card-board border-l-4 border-l-brand-gold-ink p-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <h3 className="text-sm font-display font-bold text-brand-charcoal">
                    Sign up
                  </h3>
                  <p className="text-sm text-brand-muted mt-1">
                    Choose a squad and enter this tournament.
                  </p>
                </div>
                <span className="btn-brand text-sm whitespace-nowrap flex-shrink-0">
                  Sign up
                </span>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="card-board p-6">
                <h2 className="heading-display text-xl mb-4">Events</h2>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between py-3">
                  <div className="min-w-0">
                    <h4 className="text-lg font-display font-bold text-brand-charcoal">
                      Singles
                    </h4>
                    <p className="text-sm text-brand-muted mt-0.5">
                      Singles · Scratch
                    </p>
                    <p className="text-xs text-brand-muted mt-1">Entry $45</p>
                  </div>
                  <div className="flex-shrink-0 sm:text-right">
                    <div className="font-score text-lg text-brand-charcoal">
                      $45.00
                    </div>
                    <div className="text-xs font-display tracking-wider uppercase text-brand-muted">
                      Individual
                    </div>
                  </div>
                </div>
              </div>
              <div className="card-board p-6">
                <h2 className="heading-display text-xl mb-4">Squads</h2>
                <section className="text-sm">
                  <h3 className="font-score text-brand-charcoal">Sun, Nov 8</h3>
                  <div className="mt-0.5">
                    <span className="text-brand-muted">Oak Lane Bowl</span>
                    <div className="mt-0.5 whitespace-nowrap">
                      <span className="font-score text-brand-charcoal tabular-nums">
                        9:00 AM
                      </span>{" "}
                      <span className="text-brand-charcoal">Singles</span>
                    </div>
                  </div>
                </section>
              </div>
            </div>
          </div>
        </Shot>
        ) : null}

        {show("averages") ? (
        <Shot id="averages">
          <div className="card-board overflow-hidden">
            <div className="bg-brand-charcoal text-brand-cream px-6 py-4">
              <h1 className="font-display font-bold text-xl text-brand-cream tracking-tight">
                Averages
              </h1>
              <p className="text-sm text-brand-mist mt-1">
                Verify averages before you lock scores.
              </p>
            </div>
            <DesktopTable
              tournamentBowlers={demoAverageBowlers}
              canManageAndLoggedIn
              {...demoAverageHandlers}
            />
          </div>
        </Shot>
        ) : null}
      </div>
    </>
  );
};

export default ProductShots;
